import React from 'react';
import DeliveryStatus from '../DeliveryStatus';
import { formatActivityLogDate, getCurrentFormattedTime } from '../../utils/dateUtils';
import './EditGoodieModal.css';

function EditGoodieModal({
  isOpen,
  selectedEmployee,
  goodieTypes,
  goodies,
  modalGoodieStatuses,
  modalGoodieSizes,
  modalSizeChangeReasons,
  editingSizes,
  savingSizes,
  markingCompleted,
  onClose,
  onStatusChange,
  onSizeChange,
  onSizeChangeReasonChange,
  onStartEditingSize,
  onCancelEditingSize,
  onSaveSizeChange,
  onMarkAsCollected,
  onSaveChanges
}) {
  if (!isOpen || !selectedEmployee) return null;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="edit-modal-header">
          <h2>Edit Goodie Status</h2>
          <button className="edit-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="edit-modal-body">
          <div className="employee-info">
            <p>{selectedEmployee.emp_id}</p>
            <h3>{selectedEmployee.first_name} {selectedEmployee.last_name}</h3>
          </div>
          
          <div className="goodie-cards-container">
            <div className="goodie-cards-list">
              {goodies?.map(goodieFromApi => {
                const goodieName = goodieFromApi.name;
                const goodieDisplayName = goodieName.charAt(0).toUpperCase() + goodieName.slice(1);
                
                // Find if employee has this goodie
                const employeeGoodie = selectedEmployee.goodies?.find(g => g.name === goodieName);

                const currentStatus = modalGoodieStatuses[goodieName] || (employeeGoodie?.received ? 'delivered' : 'not-delivered');
                // Get the employee's preferred size from their goodie data
                const employeePreferredSize = employeeGoodie?.size || '';
                const currentSize = modalGoodieSizes[goodieName] !== undefined ? modalGoodieSizes[goodieName] : employeePreferredSize;
                const isCollected = currentStatus === 'delivered' || employeeGoodie?.received === true;
                // Determine if this goodie type should have a size field based on the API response
                // If the size field in the API response is not null, then this goodie has sizes
                const hasSizeField = goodieFromApi.size !== null && goodieFromApi.size !== undefined;
                
                // Get available size options from the API response
                const availableSizes = hasSizeField && goodieFromApi.size ? 
                  goodieFromApi.size.split(', ').map(size => size.trim()) : [];
                
                return (
                  <div key={goodieFromApi.id || goodieName} className="goodie-item-card">
                    <div className="goodie-item-header">
                      <h3 className="goodie-item-title">{goodieDisplayName.toUpperCase()}</h3>
                      <DeliveryStatus 
                        status={currentStatus} 
                        variant="badge" 
                        size="small" 
                      />
                    </div>
                    
                    <div className="goodie-item-content">
                      {/* Goodie Details Section */}
                      {hasSizeField && (
                        <div className="goodie-details-section">
                            {/* Only show size field if the goodie has a size (not null) */}
                            
                              <div className="detail-item">
                                <span className="detail-label">Size:</span>
                                <span className="detail-value">
                                  {currentSize ? (
                                    <strong>{currentSize}</strong>
                                  ) : (
                                    <span className="no-data">Not specified</span>
                                  )}
                                </span>
                                <button 
                                  className="action-link"
                                  onClick={() => onStartEditingSize(goodieName)}
                                  title="Change size"
                                >
                                  {currentSize ? 'Change' : 'Set Size'}
                                </button>
                              </div>
                        </div>
                    )}
                      
                      {/* Action Buttons Section */}
                      <div className="goodie-actions-section">
                        {!isCollected && (
                          <button 
                            className="action-button primary"
                            onClick={() => onMarkAsCollected(goodieFromApi.id)}
                            disabled={markingCompleted[goodieFromApi.id]}
                          >
                            {markingCompleted[goodieFromApi.id] ? (
                              <>
                                <span className="loader"></span>
                                Marking...
                              </>
                            ) : (
                              'Mark as Collected'
                            )}
                          </button>
                        )}
                      </div>
                      
                      {/* Delivery Logs Section */}
                      <div className="delivery-logs">
                        <h4>Activity Log</h4>
                        <div className="log-entries">
                          {currentStatus === 'delivered' && (
                            <>
                              <div className="log-entry">
                                <span className="log-time">
                                  {formatActivityLogDate(employeeGoodie?.updated_at || employeeGoodie?.created_at)}
                                </span>
                                <span className="log-message">Goodie collected</span>
                              </div>
                            </>
                          )}
                          
                          {/* Show return activities for this goodie */}
                          {selectedEmployee.returns?.filter(returnItem => returnItem.goodies_id === employeeGoodie?.id).map((returnItem, index) => (
                            <div key={`return-${returnItem.id}`} className="log-entry">
                              <span className="log-time">
                                {formatActivityLogDate(returnItem.created_at)}
                              </span>
                              <span className="log-message">{returnItem.reason}</span>
                            </div>
                          ))}
                          
                          
                          {currentStatus === 'not-delivered' && !selectedEmployee.returns?.some(returnItem => returnItem.goodies_id === employeeGoodie?.id) && (
                            <div className="log-entry">
                              <span className="log-time">-</span>
                              <span className="log-message">No activity recorded</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Size editing modal - only show if goodie has size field */}
                    {hasSizeField && editingSizes[goodieName] && (
                      <div className="inline-edit-overlay">
                        <div className="inline-edit-content">
                          <h4>Set Size for {goodieDisplayName}</h4>
                          <div className="size-input-section">
                            <label htmlFor={`size-${goodieName}`}>Size:</label>
                            {availableSizes.length > 0 ? (
                              <select
                                id={`size-${goodieName}`}
                                value={modalGoodieSizes[goodieName] !== undefined ? modalGoodieSizes[goodieName] : (employeeGoodie?.size || '')}
                                onChange={(e) => onSizeChange(goodieName, e.target.value)}
                                className="size-input"
                                autoFocus
                              >
                                <option value="">Select a size</option>
                                {availableSizes.map(size => (
                                  <option key={size} value={size}>{size}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                id={`size-${goodieName}`}
                                type="text"
                                placeholder="Enter size (e.g., S, M, L, XL, 42, etc.)"
                                value={modalGoodieSizes[goodieName] !== undefined ? modalGoodieSizes[goodieName] : (employeeGoodie?.size || '')}
                                onChange={(e) => onSizeChange(goodieName, e.target.value)}
                                className="size-input"
                                autoFocus
                                onFocus={(e) => e.target.select()}
                              />
                            )}
                            <small className="size-help">
                              {availableSizes.length > 0 
                                ? `Available sizes: ${availableSizes.join(', ')}`
                                : 'Enter size as appropriate for this item (S/M/L/XL for clothing, shoe size numbers, etc.)'
                              }
                            </small>
                          </div>
                          
                          <div className="reason-input-section">
                            <label htmlFor={`reason-${goodieName}`}>Reason for size change:</label>
                            <textarea
                              id={`reason-${goodieName}`}
                              placeholder="Please provide a reason for changing the size..."
                              value={modalSizeChangeReasons[goodieName] || ''}
                              onChange={(e) => onSizeChangeReasonChange(goodieName, e.target.value)}
                              className="reason-textarea"
                              rows="3"
                            />
                            <small className="reason-help">
                              This information will be logged for tracking purposes.
                            </small>
                          </div>
                          
                          <div className="inline-edit-buttons">
                            <button
                              className="save-btn"
                              onClick={() => onSaveSizeChange(goodieName)}
                              disabled={!(modalGoodieSizes[goodieName] !== undefined ? modalGoodieSizes[goodieName] : (employeeGoodie?.size || '')).trim() || savingSizes[goodieName]}
                            >
                              {savingSizes[goodieName] ? (
                                <>
                                  <span className="loader"></span>
                                  Saving...
                                </>
                              ) : (
                                'Save Size'
                              )}
                            </button>
                            <button
                              className="cancel-btn"
                              onClick={() => onCancelEditingSize(goodieName)}
                              disabled={savingSizes[goodieName]}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) || []}
            </div>
          </div>
        </div>
        
        <div className="edit-modal-footer">
          <button className="edit-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="edit-modal-save" onClick={onSaveChanges}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditGoodieModal;
